import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Cake, Heart, Send, Users, PartyPopper, Gift, GraduationCap, Baby } from "lucide-react";

const CATEGORIES = ["idea", "baby", "shower", "marriage", "retirement", "graduation", "milestone"];
export { CATEGORIES };

export default function MemberCorner() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", category: "idea", subcategory: "",
    message: "", photo_url: "", consent: false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/member-corner", form);
      toast.success(t("memberCorner.thanks"));
      setForm({ name: "", email: "", category: "idea", subcategory: "", message: "", photo_url: "", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="member-corner-page"
      className="relative"
      style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.78), rgba(255,255,255,0.78)), url('https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/0ajnfwuu_cupe2.png')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
      }}
    >
      <section className="border-b border-slate-200 bg-white/40 backdrop-blur-[1px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 bg-white border border-pink-200 rounded-full px-3 py-1 text-xs text-[#d81b7a] font-semibold mb-4">
            <Users size={14} /> {t("memberCorner.badge")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("memberCorner.title")}</h1>
          <p className="text-lg text-slate-600 mt-3 max-w-2xl">{t("memberCorner.sub")}</p>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-2xl p-8 shadow-sm" data-testid="mc-ideas">
          <div className="w-14 h-14 rounded-xl cupe-gradient flex items-center justify-center mb-5"><Sparkles className="text-white" size={26} /></div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{t("memberCorner.ideasTitle")}</h2>
          <p className="text-slate-600 mt-2 leading-relaxed">{t("memberCorner.ideasBody")}</p>
          <ul className="mt-4 space-y-2 text-slate-700 text-sm">
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#d81b7a]" /> {t("memberCorner.ideaEx1")}</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#d81b7a]" /> {t("memberCorner.ideaEx2")}</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#d81b7a]" /> {t("memberCorner.ideaEx3")}</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#d81b7a]" /> {t("memberCorner.ideaEx4")}</li>
          </ul>
        </div>

        <div className="bg-white/95 backdrop-blur border border-pink-200 rounded-2xl p-8 shadow-sm" data-testid="mc-celebrate">
          <div className="w-14 h-14 rounded-xl cupe-gradient flex items-center justify-center mb-5 shadow-sm"><Cake className="text-white" size={26} /></div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{t("memberCorner.celebrateTitle")}</h2>
          <p className="text-slate-600 mt-2 leading-relaxed">{t("memberCorner.celebrateBody")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["baby", "shower", "marriage", "retirement", "graduation", "milestone"].map((k) => (
              <span key={k} className="text-xs font-semibold px-3 py-1 rounded-full bg-white border border-pink-200 text-[#d81b7a]">
                {t(`memberCorner.cat.${k}`)}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 bg-white/40 backdrop-blur-[1px]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-slate-900">{t("memberCorner.formTitle")}</h2>
          <p className="text-slate-600 mt-2">{t("memberCorner.formSub")}</p>
          <form onSubmit={submit} className="mt-8 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl p-6 md:p-8 space-y-4 shadow-sm" data-testid="mc-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">{t("memberCorner.name")} *</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px] focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none" data-testid="mc-name" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">{t("memberCorner.email")} *</span>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px] focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none" data-testid="mc-email" />
              </label>
            </div>

            <label className="block">
              <span className="text-sm font-semibold text-slate-700">{t("memberCorner.categoryLabel")} *</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md min-h-[48px] bg-white focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none" data-testid="mc-category">
                <option value="idea">{t("memberCorner.cat.idea")}</option>
                <option value="baby">{t("memberCorner.cat.baby")}</option>
                <option value="shower">{t("memberCorner.cat.shower")}</option>
                <option value="marriage">{t("memberCorner.cat.marriage")}</option>
                <option value="retirement">{t("memberCorner.cat.retirement")}</option>
                <option value="graduation">{t("memberCorner.cat.graduation")}</option>
                <option value="milestone">{t("memberCorner.cat.milestone")}</option>
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-slate-700">{t("memberCorner.message")} *</span>
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none min-h-[120px]" data-testid="mc-message" />
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-slate-700">{t("memberCorner.photo")} <span className="text-slate-400 font-normal">({t("memberCorner.optional")})</span></span>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 2 * 1024 * 1024) { toast.error(t("memberCorner.photoTooBig")); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm((s) => ({ ...s, photo_url: reader.result }));
                  reader.readAsDataURL(f);
                }}
                className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-md file:border-0 file:cupe-gradient file:text-white file:font-semibold file:cursor-pointer"
                data-testid="mc-photo"
              />
              {form.photo_url && <img src={form.photo_url} alt="preview" className="mt-3 h-24 rounded border border-slate-200" />}
            </label>

            <label className="flex items-start gap-3 p-3 rounded-md bg-pink-50/50 border border-pink-100">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#d81b7a]" data-testid="mc-consent" />
              <span className="text-sm text-slate-700">{t("memberCorner.consent")}</span>
            </label>

            <button type="submit" disabled={busy} className="cupe-gradient text-white px-6 py-3 rounded-md font-semibold min-h-[48px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="mc-submit">
              {busy ? "..." : t("memberCorner.send")} <Send size={16} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
